import { useState } from "react";
import axios from "axios";
import { INTEREST_OPTIONS } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  interest: "",
  event_date: "",
  guests: "",
  budget: "",
  message: "",
};

export default function InquiryForm({ initialInterest = "" }) {
  const [form, setForm] = useState({ ...EMPTY, interest: initialInterest });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        guests: form.guests ? Number(form.guests) : null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="inquiry-success-panel"
        className="rounded-3xl border border-forest/15 bg-white/50 px-8 py-16 text-center"
      >
        <span className="mx-auto block h-px w-14 bg-gold" aria-hidden="true" />
        <h3 className="mt-6 font-display text-3xl text-forest">Thank you.</h3>
        <p className="mt-4 text-ink/80">We'll be in touch within 48 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="rounded-3xl border border-forest/15 bg-white/50 p-6 md:p-10">
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="inq-name">Name *</label>
          <input id="inq-name" required value={form.name} onChange={set("name")} data-testid="inquiry-name-input" className="field" placeholder="Your name" />
        </div>
        <div>
          <label className="field-label" htmlFor="inq-email">Email *</label>
          <input id="inq-email" type="email" required value={form.email} onChange={set("email")} data-testid="inquiry-email-input" className="field" placeholder="you@email.com" />
        </div>
        <div>
          <label className="field-label" htmlFor="inq-phone">Phone (optional)</label>
          <input id="inq-phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="inquiry-phone-input" className="field" placeholder="(555) 555-5555" />
        </div>
        <div>
          <label className="field-label" htmlFor="inq-interest">What are you interested in? *</label>
          <select id="inq-interest" required value={form.interest} onChange={set("interest")} data-testid="inquiry-event-type-select" className="field appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232F4935%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[position:right_1rem_center] bg-no-repeat pr-10">
            <option value="" disabled>Select one…</option>
            {INTEREST_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="inq-date">Event / start date</label>
          <input id="inq-date" type="date" value={form.event_date} onChange={set("event_date")} data-testid="inquiry-date-input" className="field" />
        </div>
        <div>
          <label className="field-label" htmlFor="inq-guests">Number of guests (optional)</label>
          <input id="inq-guests" type="number" min="1" value={form.guests} onChange={set("guests")} data-testid="inquiry-guest-count-input" className="field" placeholder="e.g. 12" />
        </div>
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="inq-budget">Budget (optional)</label>
          <input id="inq-budget" value={form.budget} onChange={set("budget")} data-testid="inquiry-budget-input" className="field" placeholder="A range is helpful" />
        </div>
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="inq-message">Message</label>
          <textarea id="inq-message" rows="5" value={form.message} onChange={set("message")} data-testid="inquiry-notes-textarea" className="field resize-y" placeholder="Tell us what you have in mind…" />
        </div>
      </div>

      {error && (
        <p data-testid="inquiry-error-alert" role="alert" className="mt-5 rounded-lg bg-terracotta/10 px-4 py-3 text-sm text-terracotta">
          {error}
        </p>
      )}

      <div className="mt-8">
        <button
          type="submit"
          disabled={busy}
          data-testid="inquiry-submit-button"
          className="inline-flex cursor-pointer items-center justify-center rounded-full bg-terracotta px-10 py-3.5 text-sm font-medium tracking-wide text-cream transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-deepforest disabled:opacity-60"
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  );
}
